import React from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle, Microscope } from 'lucide-react';

/**
 * "As Featured In & Backed By" strip displayed just below the hero on the
 * marketing pages. Surfaces press credibility (BBC), industry events (Malta
 * MedTech), member-organisations (Medilink) and the Scientific Advisor.
 *
 * Props:
 *  - onPlayBBC: () => void - opens the BBC audio modal in the parent.
 */
export default function CredibilityStrip({ onPlayBBC }) {
  return (
    <section className="relative bg-white border-y border-stone-200/80 py-8 md:py-10" data-testid="featured-in-strip">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-10">
          <div className="flex items-center gap-3 shrink-0">
            <span className="h-px w-8 bg-stone-300" aria-hidden="true" />
            <span className="text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-stone-500 font-semibold">As Featured In &amp; Backed By</span>
            <span className="h-px w-8 bg-stone-300" aria-hidden="true" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 md:gap-x-10 gap-y-5">
            {/* BBC Radio Humberside - opens audio modal */}
            <button
              type="button"
              onClick={onPlayBBC}
              className="group flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-all"
              data-testid="featured-bbc-radio"
              aria-label="Play BBC Radio Humberside interview"
            >
              <span className="inline-flex items-center justify-center bg-stone-900 text-white text-[10px] md:text-[11px] font-black tracking-[0.18em] px-2 py-1 rounded-sm">BBC</span>
              <span className="font-serif text-[15px] md:text-base text-stone-700 group-hover:text-primary transition-colors">Radio Humberside</span>
              <PlayCircle className="w-4 h-4 text-stone-400 group-hover:text-primary transition-colors" />
            </button>

            <span className="hidden lg:inline-block w-px h-6 bg-stone-300" aria-hidden="true" />

            {/* Malta MedTech */}
            <Link
              to="/about"
              className="group flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity"
              data-testid="featured-malta-medtech"
              aria-label="Malta MedTech 2026 - read more on About page"
            >
              <span className="inline-flex items-center justify-center bg-emerald-700 text-white text-[10px] md:text-[11px] font-black tracking-[0.18em] px-2 py-1 rounded-sm">M&middot;MT</span>
              <span className="font-serif text-[15px] md:text-base text-stone-700 group-hover:text-primary transition-colors">Malta MedTech 2026</span>
            </Link>

            <span className="hidden lg:inline-block w-px h-6 bg-stone-300" aria-hidden="true" />

            {/* Medilink Midlands - real logo */}
            <a
              href="https://www.medilinkmidlands.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity"
              data-testid="featured-medilink"
              aria-label="Medilink Midlands Member"
            >
              <img
                src="/medilink-logo.png"
                alt="Medilink Midlands"
                className="h-9 w-auto object-contain"
                loading="lazy"
              />
              <span className="font-serif text-[15px] md:text-base text-stone-700 group-hover:text-primary transition-colors">Medilink Member</span>
            </a>

            <span className="hidden lg:inline-block w-px h-6 bg-stone-300" aria-hidden="true" />

            {/* Dr Benny Antony - Scientific Advisor */}
            <Link
              to="/about#scientific-advisor"
              className="group flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity"
              data-testid="featured-scientific-advisor"
              aria-label="Dr. Benny Antony - Scientific Advisor"
            >
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
                <Microscope className="w-4 h-4" />
              </span>
              <span className="leading-tight">
                <span className="block font-serif text-[15px] md:text-base text-stone-700 group-hover:text-primary transition-colors">Dr. Benny Antony, PhD</span>
                <span className="block text-[10px] md:text-[11px] tracking-wider uppercase text-stone-500">Scientific Advisor</span>
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
